import { Box, Button, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import React, { useState } from 'react';
import AssetImage from '../../../components/AssetImage';

export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

interface Props {
  logoFile: string | null;
  error?: string;
  busy: boolean;
  editable: boolean;
  onUpload: (file: File) => void;
  showTitle?: boolean;
  readOnlyText?: string;
}

/** Border colour: highlighted while dragging, red with a problem, otherwise the outlined-input grey. */
function borderColour(theme: Theme, dragging: boolean, problem: string) {
  if (dragging) return 'primary.main';
  if (problem) return 'error.main';
  return theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)';
}

function LogoPreview({ logoFile }: { logoFile: string | null }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: 120,
        height: 56,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      {logoFile ? (
        <AssetImage
          url={logoFile}
          alt="Current logo"
          sx={{ maxWidth: 110, maxHeight: 48, objectFit: 'contain' }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary">
          No logo
        </Typography>
      )}
    </Box>
  );
}

interface InstructionsProps {
  logoFile: string | null;
  busy: boolean;
  editable: boolean;
  problem: string;
  readOnlyText?: string;
  accept: (file: File | undefined) => void;
}

/** What to do (drag or choose a file), or the read-only state, with the problem or hint below. */
function LogoInstructions({
  logoFile,
  busy,
  editable,
  problem,
  readOnlyText,
  accept,
}: InstructionsProps) {
  return (
    <Box sx={{ minWidth: 0 }}>
      {editable ? (
        <Typography variant="body2">
          {busy ? 'Uploading…' : 'Drag a logo here, or '}
          {!busy && (
            <Button size="small" component="label" sx={{ p: 0, minWidth: 0 }}>
              {logoFile ? 'choose a new file' : 'choose a file'}
              <input
                hidden
                type="file"
                accept={LOGO_TYPES.join(',')}
                onChange={(event) => {
                  accept(event.target.files?.[0]);
                  event.target.value = '';
                }}
              />
            </Button>
          )}
        </Typography>
      ) : (
        <Typography variant="body2">
          {readOnlyText ?? (logoFile ? 'Logo uploaded' : 'No logo uploaded')}
        </Typography>
      )}
      {(editable || problem) && (
        <Typography variant="caption" color={problem ? 'error' : 'text.secondary'} component="div">
          {problem || 'PNG, JPG or WebP. Saved as soon as it uploads.'}
        </Typography>
      )}
    </Box>
  );
}

/** Drop a logo image on the zone, or choose one, to upload it straight away. */
export default function LogoDropZone({
  logoFile,
  error,
  busy,
  editable,
  onUpload,
  showTitle = true,
  readOnlyText,
}: Props) {
  const [dragging, setDragging] = useState(false);
  const [typeProblem, setTypeProblem] = useState('');
  const problem = typeProblem || (error ? `Logo not uploaded: ${error}` : '');
  const accept = (file: File | undefined) => {
    if (!file) return;
    if (!LOGO_TYPES.includes(file.type)) {
      setTypeProblem('Use a PNG, JPG or WebP image.');
      return;
    }
    setTypeProblem('');
    onUpload(file);
  };
  const active = editable && !busy;
  return (
    <Box>
      {showTitle && (
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Logo
        </Typography>
      )}
      <Box
        data-testid="logo-drop-zone"
        onDragOver={(event) => {
          if (!active) return;
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (active) accept(event.dataTransfer.files?.[0]);
        }}
        sx={(theme) => ({
          border: 1,
          borderColor: borderColour(theme, dragging, problem),
          bgcolor: dragging ? 'action.hover' : 'transparent',
          borderRadius: `${theme.shape.borderRadius}px`,
          p: 2,
          '&:hover': active && !dragging && !problem ? { borderColor: 'text.primary' } : undefined,
        })}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <LogoPreview logoFile={logoFile} />
          <LogoInstructions
            logoFile={logoFile}
            busy={busy}
            editable={editable}
            problem={problem}
            readOnlyText={readOnlyText}
            accept={accept}
          />
        </Stack>
      </Box>
    </Box>
  );
}
